import type Graph from 'graphology';
import willCreateCycle from 'graphology-dag/will-create-cycle';
import type { Connection } from 'reactflow';

import {
  isNotNullConnection,
  resolveConnection,
  validateConnection,
} from './utils';
import {
  isTargetNodeDependantOnSourceNode,
  reverseConnection,
} from '../../features/graph/helpers';
import type { LabwareByHandleIndexByTaskId } from '../../features/selectors/types';
import {
  getTaskLabwareCorrespondence,
  isDataConnection,
  isLabwareDependencyConnection,
  isTimeConstraintConnection,
} from '../../utils';

export const _isValidConnection = ({
  graph,
  labwareByHandleIndexByTaskId,
  connection: rawConnection,
}: {
  graph: Graph;
  labwareByHandleIndexByTaskId: LabwareByHandleIndexByTaskId;
  connection: Connection;
}) => {
  const isBackwardsTimeConstraint =
    isNotNullConnection(rawConnection) &&
    isTimeConstraintConnection(rawConnection) &&
    isTargetNodeDependantOnSourceNode(
      graph,
      rawConnection.target,
      rawConnection.source
    ) &&
    !isTargetNodeDependantOnSourceNode(
      graph,
      rawConnection.source,
      rawConnection.target
    );

  // Rectify connection before checking circularity.
  // (if the connection is created, it will still need to be rectified later by rectifyTimeConstraints)
  const ctn = isBackwardsTimeConstraint
    ? reverseConnection(rawConnection)
    : rawConnection;

  const connection = validateConnection(ctn);
  if (connection === null) {
    return false;
  }

  const resolvedConn = resolveConnection(connection);

  if (willCreateCycle(graph, resolvedConn.source, resolvedConn.target)) {
    return false;
  }

  if (isTimeConstraintConnection(connection)) {
    return (
      getTaskLabwareCorrespondence(
        connection.source,
        connection.target,
        labwareByHandleIndexByTaskId
      ) !== 'DIFFERENT_LABWARE'
    );
  }

  if (isLabwareDependencyConnection(connection)) {
    return true;
  }

  return isDataConnection(resolvedConn);
};
